import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Accessibility, ChevronLeft } from 'lucide-react';

const AccessibilityStatementPage = () => {
  return (
    <div className="min-h-screen bg-black text-apple-100 py-24">
      <Helmet>
        <title>Accessibility Statement - Sortio</title>
        <meta name="description" content="Sortio's commitment to accessibility, our conformance target (WCAG 2.1 AA), known limitations, and how to report issues." />
      </Helmet>

      <div className="absolute top-0 inset-x-0 h-[500px] bg-gradient-to-b from-apple-700/10 to-transparent"></div>

      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        <Link to="/" className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-12 transition-colors group">
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </Link>

        <div className="mb-16 flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <Accessibility className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-center">
            Accessibility <span className="apple-gradient-text">Statement</span>
          </h1>
          <p className="text-apple-300 mt-4 text-center max-w-2xl">
            We want Sortio to be usable by everyone. This statement explains our commitment, our progress, and where we know we still fall short.
          </p>
        </div>

        <div className="apple-panel p-8 md:p-12 rounded-apple backdrop-blur-sm mb-12">
          <div className="space-y-10 text-apple-300 leading-relaxed">
            <p className="text-sm text-apple-400"><em>Date of statement: 16 May 2026</em></p>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Conformance target</h2>
              <p>
                We aim for the Sortio marketing site (getsortio.com) and the desktop application to conform to the{' '}
                <a href="https://www.w3.org/TR/WCAG21/" target="_blank" rel="noopener noreferrer" className="text-sortio-primary hover:text-sortio-primary/80">
                  Web Content Accessibility Guidelines (WCAG) 2.1 Level AA
                </a>
                . The marketing site is the focus of the current pass; the desktop app accessibility work is on a parallel track.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Measures we have taken</h2>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Set <code>lang="en"</code> on the HTML root to support assistive technologies.</li>
                <li>Audited all marketing pages for missing alt text and added descriptive alternatives.</li>
                <li>Added <code>prefers-reduced-motion</code> guards around hero animations, parallax effects, and floating elements.</li>
                <li>Reviewed color contrast for body text, links, and CTAs against Apple-style dark backgrounds.</li>
                <li>Associated all form inputs (consent banner, opt-out form, contact form) with labels via either <code>htmlFor</code> or <code>aria-label</code>.</li>
                <li>Ensured all interactive elements are keyboard reachable and show a visible focus ring.</li>
              </ul>
              <p>
                A detailed log of each fix is available in our repository at{' '}
                <code>docs/ACCESSIBILITY_PASS.md</code> (link available on request).
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Known limitations</h2>
              <p>We are tracking the following issues; expect fixes in upcoming releases:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>The animated 3D hero on the homepage may not fully respect <code>prefers-reduced-motion</code> in older browsers (specifically, browsers without the <code>matchMedia</code> support we rely on for the early return). Users on those browsers will see a CSS fallback background that still includes light pulse and float animations.</li>
                <li>Some persona landing pages contain auto-playing background videos. They are muted and have a poster image, but they may not pause automatically for users who request reduced motion outside of our framework.</li>
                <li>A small number of decorative gradient orbs are not yet hidden from assistive technologies; they have no functional content but are exposed in the DOM.</li>
                <li>The session-replay tool (Microsoft Clarity), when enabled by user consent, may capture pointer interactions outside our control.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Tooling used</h2>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>axe DevTools (Chrome extension) for spot checks.</li>
                <li>Lighthouse accessibility audit.</li>
                <li>Manual keyboard navigation and screen-reader (VoiceOver) review of the highest-traffic pages.</li>
                <li>Static code review of section components for landmark structure, heading hierarchy, and image alt coverage.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Reporting an issue</h2>
              <p>
                If you encounter an accessibility barrier, email{' '}
                <a href="mailto:marcus@getsortio.com" className="text-sortio-primary hover:text-sortio-primary/80">marcus@getsortio.com</a>{' '}
                with the page URL, a short description, and your assistive technology (if relevant). We aim to respond within 5 business days and to fix WCAG 2.1 AA failures within 30 days where feasible. If the fix takes longer, we will propose a workaround in the meantime.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Formal complaints</h2>
              <p>
                If we cannot resolve your concern, US users may contact the United States Access Board (<a href="https://www.access-board.gov/" target="_blank" rel="noopener noreferrer" className="text-sortio-primary hover:text-sortio-primary/80">access-board.gov</a>) and EU users may contact their national equality body. UK users may contact the Equality and Human Rights Commission (<a href="https://www.equalityhumanrights.com/" target="_blank" rel="noopener noreferrer" className="text-sortio-primary hover:text-sortio-primary/80">equalityhumanrights.com</a>).
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityStatementPage;
